import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Huntington Beach", cities: "Centro, Sunset Beach, Bolsa Chica, Pacific City" },
    { zone: "Westminster", cities: "Westminster, Garden Grove, Midway City" },
    { zone: "Fountain Valley", cities: "Fountain Valley, Seal Beach, Los Alamitos" },
    { zone: "Costa Mesa", cities: "Costa Mesa, Santa Ana, Newport Beach" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Vecindarios que Servimos en Huntington Beach</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Huntington Beach, Westminster, Fountain Valley y ciudades vecinas.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div key={i} className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")} style={{ transitionDelay: `${i * 100}ms` }}>
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar en Huntington Beach</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const HuntingtonBeachSpanishPage = () => (
  <LocationContext.Provider value={{ slug: "huntington-beach", name: "Huntington Beach", type: "city", regionSlug: "orange-county" }}>
    <SEO
      title="Limpieza del Hogar en Huntington Beach, CA — Uplevel Cleaning"
      description="Servicio profesional de limpieza del hogar en Huntington Beach, CA. Precios planos, reserva en 60 segundos, garantía del 200%. Limpieza estándar, profunda y de mudanza."
      keywords="limpieza huntington beach, maid service huntington beach español, servicio de limpieza huntington beach ca, empleada doméstica huntington beach"
      url="https://uplevelcleaning.com/es/huntington-beach"
      locationName="Huntington Beach"
      regionSlug="orange-county"
      lang="es"
      includeLocalBusiness={true}
      includeFAQ={true}
      breadcrumbs={[{ name: "Inicio", url: "/" }, { name: "Orange County", url: "/es/orange-county" }, { name: "Huntington Beach", url: "/es/huntington-beach" }]}
      reviews={[
        { author: "Claudia P.", rating: 5, reviewBody: "Vivo cerca de la playa y la arena entra por todos lados. Uplevel lo deja impecable cada vez. ¡Totalmente recomendado!", datePublished: "2026-03-10" },
        { author: "Rodrigo M.", rating: 5, reviewBody: "Servicio puntual y muy profesional en Huntington Beach. El precio fue exactamente como lo cotizaron en línea. Sin sorpresas.", datePublished: "2026-02-05" },
      ]}
    />
    <div className="flex min-h-screen flex-col" lang="es">
      <StickyBanner />
      <Navbar />
      <main className="flex-1">
        <SpanishHero cityName="Huntington Beach" regionSlug="orange-county" />
        <div className="bg-primary/5 border-b border-primary/10 py-4">
          <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
            {["Servicio Local en Huntington Beach", "Surf City — Limpieza de Playa Especializada", "Garantía del 200%", "Reserva en 60 Segundos"].map((item, i) => (
              <span key={i} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {item}</span>
            ))}
          </div>
        </div>
        <SpanishWhyUs cityName="Huntington Beach" />
        <NearbyAreas />
        <SpanishServices />

        <FrequencyDiscounts />
        <PricingCalculator />
        <SpanishTestimonials region="orange-county" />
        <Guarantee />
        <SpanishFAQ cityName="Huntington Beach" />
        <InternalLinks />
        <SpanishFinalCTA cityName="Huntington Beach" />
      </main>
      <Footer />
    </div>
  </LocationContext.Provider>
);

export default HuntingtonBeachSpanishPage;
